import {
  Fan,
  Gauge,
  Home,
  Wallet,
  AlertTriangle,
  Sparkles,
  Ruler,
  CalendarClock,
  ListChecks,
  Image as ImageIcon,
} from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import PageHero from "@/components/site/PageHero";
import ServiceFAQ from "@/components/site/ServiceFAQ";
import RelatedServices from "@/components/site/RelatedServices";
import FinalCTA from "@/components/site/FinalCTA";
import AreaPills from "@/components/site/AreaPills";
import DemoNotice from "@/components/site/DemoNotice";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/51ddf5f6-e80c-4259-9ef6-c5393e7b5903/images/dd575c7557e7d72528f8d94bb53752a0ac5d546bb142e6d9098a6c4d0ab73cf8.png";

const SIGNS = [
  {
    icon: CalendarClock,
    title: "System is 12+ years old",
    desc: "Around the 12–15 year mark, repair value drops fast and efficiency gains from a new system pay off.",
  },
  {
    icon: AlertTriangle,
    title: "Major component failure",
    desc: "Compressor, heat exchanger, or repeated refrigerant issues often tilt the math toward replacement.",
  },
  {
    icon: Wallet,
    title: "Energy bills climbing",
    desc: "Bills creeping up year over year, especially in summer, often signal a system losing efficiency.",
  },
  {
    icon: Home,
    title: "Comfort is uneven",
    desc: "Hot rooms, cold rooms, and humidity that won't leave often point to a sizing or system-age issue.",
  },
  {
    icon: Fan,
    title: "Frequent repair calls",
    desc: "If you've been calling for service multiple times a year, the savings from a new system add up quickly.",
  },
  {
    icon: Gauge,
    title: "Old refrigerant or SEER",
    desc: "Older units running outdated refrigerants or low SEER ratings cost more to run than they should.",
  },
];

const INCLUDED = [
  "Load calculation to right-size the new system",
  "Walk-through of efficiency tiers (SEER / SEER2)",
  "Single-stage, two-stage, and variable-speed options",
  "Match between indoor air handler and outdoor unit",
  "Refrigerant type and code-compliant install",
  "Duct evaluation (existing duct quality, leakage, return air)",
  "Thermostat and zoning options",
  "Removal and disposal of the old system",
  "Permit pull and inspection coordination",
];

const TELL_US = [
  {
    icon: Ruler,
    title: "Square footage and home layout",
    desc: "Approximate square footage, number of floors, ceiling height — anything that helps with sizing.",
  },
  {
    icon: ListChecks,
    title: "What's not working today",
    desc: "Uneven cooling, humidity, noisy unit, high bills — the symptoms shape the recommendation.",
  },
  {
    icon: ImageIcon,
    title: "Photos of the existing system",
    desc: "The outdoor unit, indoor air handler, and the model/serial sticker are all useful for a quote.",
  },
];

const FAQS = [
  {
    q: "When should I repair vs replace my AC system?",
    a: "If your system is under 10 years old and the repair is a fraction of replacement cost, repair usually wins. Past 12–15 years — especially with multiple service calls, rising bills, or compressor/refrigerant issues — replacement often pays off in efficiency and reliability. A good contractor will lay both numbers out honestly.",
  },
  {
    q: "How long does an AC replacement take?",
    a: "A typical residential changeout takes one full day for the install itself, plus permit and inspection time depending on the local authority. More complex jobs — duct work, electrical upgrades, multiple systems — can take longer.",
  },
  {
    q: "What efficiency rating should I look for?",
    a: "In Florida, higher efficiency (higher SEER2 numbers, variable-speed systems) tends to pay off because cooling seasons are long. The right tier depends on your home and how long you plan to stay. Ask about both 'good / better / best' options.",
  },
  {
    q: "How do you determine the right system size?",
    a: "A real contractor performs a load calculation (often a Manual J) based on square footage, insulation, windows, orientation, ductwork, and local climate. Oversized systems short-cycle and leave humidity behind — undersized ones run constantly and never quite catch up.",
  },
  {
    q: "Do you offer financing?",
    a: "Ask about financing options — a real contractor can typically walk you through monthly options. This demo site doesn't promise specific pricing, but the intake captures interest so the team can follow up.",
  },
  {
    q: "Is this a real HVAC company?",
    a: 'Sun Chaser Cooling is a sample HVAC website created by PrismForge Digital. <a class="text-aqua-deep underline-offset-2 hover:underline" href="/demo">See how this demo works →</a>',
    html: true,
  },
];

export default function Replacement() {
  useSeo({
    title:
      "AC Replacement in Southwest Florida | Sun Chaser Cooling — PrismForge Digital Demo",
    description:
      "Demo AC replacement page for SWFL. Signs your system is at end of life, sizing for Florida humidity, efficiency basics, and a quote-ready intake flow — built by PrismForge Digital.",
  });

  return (
    <div data-testid="replacement-page">
      <PageHero
        eyebrow="System Replacement"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Services", to: "/services" },
          { label: "Replacement" },
        ]}
        title="AC replacement for"
        highlight="Southwest Florida homes."
        subtitle="When repairs stop adding up — right-sized swap-outs, efficiency basics, and a quote-ready intake that respects your time."
        primaryCta={{
          label: "Request Replacement Estimate",
          to: "/request-service?intent=replacement",
        }}
        secondaryCta={{ label: "Request HVAC Service", to: "/request-service" }}
        image={HERO_IMG}
        imageAlt="Southwest Florida home at golden hour showing where a residential AC replacement would take place."
        variant="light"
        testId="replacement-hero"
      />

      {/* Signs */}
      <section
        data-testid="replacement-signs-section"
        aria-labelledby="replacement-signs-heading"
        className="relative bg-white py-20 sm:py-24"
      >
        <div className="container-page">
          <div className="max-w-2xl">
            <span className="pill">
              <AlertTriangle className="h-3 w-3 text-sun" />
              When replacement makes sense
            </span>
            <h2
              id="replacement-signs-heading"
              className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl"
            >
              Signs it&rsquo;s time to think about a new system.
            </h2>
            <p className="mt-4 text-base text-navy/70 sm:text-lg">
              No single signal means &ldquo;replace tomorrow.&rdquo; A few of
              these together usually do.
            </p>
          </div>
          <ul className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SIGNS.map((s) => {
              const Icon = s.icon;
              const slug = s.title
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/(^-|-$)/g, "");
              return (
                <li
                  key={s.title}
                  data-testid={`replacement-sign-${slug}`}
                  className="rounded-2xl border border-mist-line bg-white p-6 shadow-soft transition-transform hover:-translate-y-1"
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-navy text-aqua">
                    <Icon className="h-4 w-4" />
                  </span>
                  <h3 className="mt-4 font-display text-lg font-bold leading-tight tracking-tight text-navy">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-navy/70">
                    {s.desc}
                  </p>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* What's included */}
      <section
        data-testid="replacement-included-section"
        aria-labelledby="replacement-included-heading"
        className="relative bg-mist py-20 sm:py-24"
      >
        <div className="container-page grid grid-cols-1 items-start gap-12 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-5">
            <span className="pill">
              <Sparkles className="h-3 w-3 text-aqua" />
              What&rsquo;s included
            </span>
            <h2
              id="replacement-included-heading"
              className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl"
            >
              A real replacement scope.
            </h2>
            <p className="mt-4 max-w-lg text-base text-navy/70 sm:text-lg">
              Replacement isn&rsquo;t just &ldquo;swap the box.&rdquo; A
              proper job touches sizing, ductwork, controls, and code &mdash;
              all of it adds up to comfort and efficiency.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="rounded-2xl border border-mist-line bg-white shadow-soft">
              {INCLUDED.map((line, i) => (
                <li
                  key={line}
                  className={`flex items-center justify-between gap-3 px-5 py-4 ${
                    i < INCLUDED.length - 1 ? "border-b border-mist-line" : ""
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-aqua/15 font-display text-xs font-bold text-aqua-deep">
                      {i + 1}
                    </span>
                    <p className="text-[15px] font-medium text-navy/85">
                      {line}
                    </p>
                  </div>
                  <span className="hidden text-[10px] font-semibold uppercase tracking-[0.16em] text-navy/40 sm:inline">
                    Included
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs text-navy/55">
              Demo overview &mdash; exact scope depends on the home and
              contractor.
            </p>
          </div>
        </div>
      </section>

      {/* What to tell us */}
      <section
        data-testid="replacement-tell-section"
        aria-labelledby="replacement-tell-heading"
        className="relative bg-white py-20 sm:py-24"
      >
        <div className="container-page">
          <div className="max-w-2xl">
            <span className="pill">
              <ListChecks className="h-3 w-3 text-aqua" />
              What to tell us
            </span>
            <h2
              id="replacement-tell-heading"
              className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl"
            >
              Quote-ready details upfront.
            </h2>
            <p className="mt-4 text-base text-navy/70 sm:text-lg">
              The faster a contractor can scope your home, the faster you get
              a real number to compare.
            </p>
          </div>
          <ul className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
            {TELL_US.map((t) => {
              const Icon = t.icon;
              return (
                <li
                  key={t.title}
                  className="flex flex-col rounded-2xl border border-mist-line bg-white p-6 shadow-soft"
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-navy text-aqua">
                    <Icon className="h-4 w-4" />
                  </span>
                  <h3 className="mt-4 font-display text-lg font-bold leading-tight tracking-tight text-navy">
                    {t.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-navy/70">
                    {t.desc}
                  </p>
                </li>
              );
            })}
          </ul>
          <div className="mt-8">
            <DemoNotice
              variant="dark"
              label="No pricing guarantees — quotes come from a real contractor after a load calculation."
            />
          </div>
        </div>
      </section>

      {/* SWFL context */}
      <section
        data-testid="replacement-swfl-section"
        aria-labelledby="replacement-swfl-heading"
        className="relative bg-mist py-20 sm:py-24"
      >
        <div className="container-page max-w-4xl">
          <span className="pill">
            <span className="h-1.5 w-1.5 rounded-full bg-aqua" />
            Southwest Florida context
          </span>
          <h2
            id="replacement-swfl-heading"
            className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl"
          >
            SWFL homes are tougher on HVAC systems.
          </h2>
          <div className="mt-5 space-y-4 text-base leading-relaxed text-navy/75 sm:text-lg">
            <p>
              Coastal salt air, long cooling seasons, and high humidity all
              shorten the effective life of HVAC equipment compared with
              cooler climates. A 12-year-old system in SWFL has often done the
              cooling work of a 16-year-old system up north.
            </p>
            <p>
              Right-sized replacements matter more here, too. An oversized
              system in a humid climate cools the temperature but leaves the
              humidity behind &mdash; which is why the room feels &ldquo;cold
              and clammy&rdquo; instead of comfortable.
            </p>
          </div>
          <div className="mt-8">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-navy/55">
              Replacement coverage across SWFL
            </p>
            <AreaPills className="mt-3" />
          </div>
        </div>
      </section>

      <ServiceFAQ
        title="Common replacement questions."
        subtitle="Honest answers to the questions that come up before every quote."
        faqs={FAQS}
        testId="replacement-faq"
      />

      <RelatedServices currentSlug="replacement" />

      <FinalCTA
        title="Get a real replacement estimate."
        subtitle="Start the intake — share the basics and a contractor will reach out to schedule the in-home assessment."
        primaryCta={{
          label: "Request Replacement Estimate",
          to: "/request-service?intent=replacement",
          icon: Fan,
        }}
        secondaryCta={{
          label: "See related services",
          to: "/services",
        }}
      />
    </div>
  );
}
